<script setup lang="ts">
import MmFeedbackState from '~/components/mm/base/MmFeedbackState.vue';
import MmChip from '~/components/mm/base/MmChip.vue';
import MmButton from '~/components/mm/base/MmButton.vue';
import '~/components/mm/music/listening.css';
import type { Music, GenMusic } from '~/services/musicService';
import { musicService } from '~/services/musicService';
import { getSectionByPath, generateTitleFromPath, getSectionById } from '~/services/musicSectionMapConfig';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter, ref, onMounted, onUnmounted, watch, useHead, computed } from '#imports';
import { usePagination } from '~/composables/usePagination';
import MusicList from '~/components/mm/music/CollectionSongs.vue';
import CollectionActions from '~/components/mm/music/CollectionActions.vue';
import ExploreHeader from '~/components/mm/music/ExploreHeader.vue';
import ChartRail from '~/components/mm/music/ChartRail.vue';
import GenreDirectory from '~/components/mm/music/GenreDirectory.vue';
import { collectionLabel } from '~/components/mm/music/listeningPresentation.mjs';
import FeedEntry from '~/components/mm/music/FeedEntry.vue';
import { useAuthState } from '~/composables/useAuthState';
import { useToast } from '~/composables/useToast';
import { useMusicCreate } from '~/composables/useMusicCreate';
import { trackEvent } from '~/utils/analytics';
import { usePageReadingPosition } from '~/composables/usePageReadingPosition';
import { rankingPage, rankingSummary, attributeLegacy } from '~/components/mm/music/rankings';
import { rankingKey, rankingLabel, rankingRequestKey } from '~/components/mm/music/ranking-contract.mjs';
import { isLegacyRankingUnsupported } from '~/components/mm/music/discovery-source.mjs';
import { loadExploreData } from '~/utils/explore-entry.mjs';
import DailyChallenge from '~/components/mm/music/DailyChallenge.vue';
import { DAILY_COMPETITION } from '~/components/mm/music/competition.mjs';
import type { CompetitionSnapshot } from '~/components/mm/music/competition-types';
import RankingUpdated from '~/components/mm/music/RankingUpdated.vue';

const { user, userAccount, handleSignIn } = useAuthState();
const { saveMusicData } = useMusicCreate();
const toast = useToast();

const { t, te, locale } = useI18n();
const route = useRoute();
const router = useRouter();

// 状态
const chartTitle = ref('');
const exploreQuery = ref('');
const sectionParam = route.params.section as string;

// 判断URL类型：新的/music/[section]格式，或者旧的查询参数格式
const isLegacyUrl = !sectionParam && (route.query.chart || route.query.category);
const sectionInfo = sectionParam ? getSectionByPath(sectionParam) : null;

// 设置标题
if (sectionParam && !isLegacyUrl) {
  // Ranking keys have no catalog entry; never surface the raw key (e.g. "Ranking Music_daily_competition").
  const rankingSeoTitles: Record<string, string> = { 'ranking-music_daily_competition': 'Daily New Song Challenge' };
  const rankingPathTitle = sectionParam.startsWith('ranking-')
    ? rankingSeoTitles[sectionParam] || generateTitleFromPath(sectionParam.replace(/^ranking-(music_)?/, '').replace(/_/g, '-'))
    : '';
  chartTitle.value = rankingPathTitle || sectionInfo?.title || generateTitleFromPath(sectionParam);
} else {
  chartTitle.value = route.query.title as string;
}

// 确定section类型
let sectionType: 'category' | 'rank' = 'category';
if (sectionParam && sectionInfo) {
  sectionType = sectionInfo.type as 'category' | 'rank';
} else if (route.query.chart) {
  sectionType = 'rank';
} else if (route.query.category) {
  sectionType = 'category';
}

// Localized labels never become a draft's style or prompt defaults.
const displayTitle = computed(() => !isLegacyUrl ? collectionLabel(sectionParam, sectionRankingKey ? rankingLabel({ ranking_key: sectionRankingKey, title_key: `ranking.${sectionRankingKey}` }, t, te) : chartTitle.value, t, te) : chartTitle.value);
const categoryLabel = (category: { path: string; title: string }) => collectionLabel(category.path, category.title, t, te);

// 分页相关状态
const currentPage = ref(parseInt(route.query.page as string) || 1);
const totalPages = ref(1);
const PAGE_SIZE = 20;
const SSR_API_TIMEOUT_MS = 6500;
const sectionRankingKey = rankingKey(sectionParam || '');
// New base/language charts share the chart UI while existing head metadata
// continues to use the original section mapping.
const isChart = sectionType === 'rank' || !!sectionRankingKey && !sectionRankingKey.startsWith('music_genre_');
const windowEnd = ref(0);
const legacyPage = async (page: number) => sectionInfo?.type === 'rank'
  ? musicService.getMoreChartMusic(sectionInfo.id, sectionInfo.selected_option, { page, size: PAGE_SIZE }, { timeout: SSR_API_TIMEOUT_MS, musicmintInlineError: true })
  : musicService.getMoreCategoryMusic(sectionParam, { page, size: PAGE_SIZE }, { timeout: SSR_API_TIMEOUT_MS, musicmintInlineError: true });
const sectionPage = async (page: number, requestedLocale = locale.value) => {
  if (sectionRankingKey) {
    try {
      // An empty V3 result is final. Only an old server (404/426) uses legacy endpoints.
      return await rankingPage(sectionRankingKey, page, PAGE_SIZE, requestedLocale);
    } catch (cause) {
      if (sectionRankingKey === DAILY_COMPETITION || !isLegacyRankingUnsupported(cause)) throw cause;
    }
  }
  let response = attributeLegacy(await legacyPage(page));
  if (!response.data.list.length && sectionRankingKey) response = attributeLegacy(await musicService.getMoreChartMusic(
    getSectionByPath('global-trending')!.id, 'Global', { page, size: PAGE_SIZE }, { timeout: SSR_API_TIMEOUT_MS, musicmintInlineError: true },
  ));
  return { ...response, data: { ...response.data, window_end: 0 } };
};

// Preserve SSR content while starting the primary list and auxiliary navigation together.
const { data: ssrBundle } = await useAsyncData(
  rankingRequestKey(sectionParam, currentPage.value, locale.value),
  () => loadExploreData(
    () => !sectionParam || isLegacyUrl || (!sectionInfo && !sectionRankingKey) ? null : sectionPage(currentPage.value),
    () => rankingSummary(locale.value),
    locale.value,
  ),
);
const ssrResponse = computed(() => ssrBundle.value?.response);
const ssrSectionFailed = computed(() => ssrBundle.value?.failed === true);
const competition = ref<CompetitionSnapshot | undefined>((ssrResponse.value as any)?.data);
const isDailyChallenge = sectionRankingKey === DAILY_COMPETITION;

// 从 SSR 响应中提取初始数据
const extractInitialData = () => {
  if (!ssrResponse.value) return undefined;
  const response = ssrResponse.value as any;
  let items: Music[] = [];
  let totalCount = 0;

  if (Array.isArray(response.data)) {
    items = response.data;
  } else if (response.data && Array.isArray(response.data.list)) {
    items = response.data.list;
  }

  if (response.data && typeof response.data.total === 'number') {
    totalCount = response.data.total;
  } else if (typeof response.total === 'number') {
    totalCount = response.total;
  }

  return { list: items, total: totalCount };
};

const ssrInitialData = extractInitialData();
windowEnd.value = (ssrResponse.value as any)?.data?.window_end || windowEnd.value;

// 用 SSR 数据初始化 totalPages
if (ssrInitialData && ssrInitialData.total > 0) {
  totalPages.value = Math.ceil(ssrInitialData.total / PAGE_SIZE);
}

// 动态生成获取数据的函数
let sectionRequestVersion = 0;
const fetchMusicData = async (pageNum: number) => {
  const requestedLocale = locale.value;
  const request = ++sectionRequestVersion;
  if (sectionParam && !isLegacyUrl && (sectionInfo || sectionRankingKey)) {
    const response = await sectionPage(pageNum, requestedLocale);
    if (request !== sectionRequestVersion || requestedLocale !== locale.value) return response;
    competition.value = response.data;
    windowEnd.value = response.data?.window_end || 0;
    const total = response?.data?.total ?? response?.total;
    if (typeof total === 'number' && Number.isFinite(total) && total >= 0) totalPages.value = Math.max(1, Math.ceil(total / PAGE_SIZE));
    return response;
  } else {
    const chartId = route.query.chart as string;
    const category = route.query.category as string;

    if (chartId) {
      const response = await musicService.getMoreChartMusic(chartId, undefined, { page: pageNum, size: PAGE_SIZE });
      const total = response?.data?.total ?? response?.total;
      if (typeof total === 'number' && Number.isFinite(total) && total >= 0) {
        totalPages.value = Math.max(1, Math.ceil(total / PAGE_SIZE));
      }
      return response;
    } else if (category) {
      const response = await musicService.getMoreCategoryMusic(category, { page: pageNum, size: PAGE_SIZE });
      const total = response?.data?.total ?? response?.total;
      if (typeof total === 'number' && Number.isFinite(total) && total >= 0) {
        totalPages.value = Math.max(1, Math.ceil(total / PAGE_SIZE));
      }
      return response;
    } else {
      throw new Error(t('music.invalid_params'));
    }
  }
};

// 使用分页hooks，传入 SSR 初始数据避免客户端重复请求
const {
  list: musicList,
  isLoading,
  isLoadingMore,
  error,
  loadMoreTrigger,
  loadData,
  hasMore,
  loadMore
} = usePagination<Music & { cover_hue?: number }>(
  fetchMusicData,
  {
    pageSize: PAGE_SIZE,
    requestKey: () => rankingRequestKey(sectionParam, currentPage.value, locale.value),
    initialPage: currentPage.value,
    initialData: ssrInitialData,
    infiniteScroll: false,
    appendPages: false,
    errorHandler: (err) => {
      console.error(`加载音乐数据失败:`, err);
      error.value = t('common.load_failed');
    }
  }
);

if (ssrSectionFailed.value) error.value = t('common.load_failed');

watch(locale, () => {
  competition.value = undefined;
  windowEnd.value = 0;
  totalPages.value = 1;
  void loadData(currentPage.value);
});

const readingPath = route.path;
const { pageRoot: sectionRoot, pageHeading: sectionHeading, restorePending } = usePageReadingPosition(() => readingPath, { resetOnQueryChange: true, ready: () => !isLoading.value });
watch(isLoading, loading => { if (!loading) void restorePending(); });

// SEO配置
const { setMusicSectionSeo } = useSeo();
setMusicSectionSeo({
  title: displayTitle.value,
  type: sectionType,
  path: route.path,
  page: currentPage.value,
});

// Metadata and the heading use the same translated category label.
useSeoMeta({
  title: () => t('meta.musicSection.title', { section: displayTitle.value }),
  description: () => t(`meta.musicSection.description_${sectionType}`, { section: displayTitle.value }),
  ogTitle: () => t(`meta.musicSection.ogTitle_${sectionType}`, { section: displayTitle.value }),
  ogDescription: () => t(`meta.musicSection.ogDescription_${sectionType}`, { section: displayTitle.value }),
});
const seoIntroText = computed(() => t(
  isChart ? 'ux.collectionUi.rankIntro' : 'ux.collectionUi.genreIntro',
  { name: displayTitle.value },
));

// JSON-LD structured data - 基于 SSR 后的 musicList
const structuredData = computed(() => {
  const baseUrl = 'https://musicmint.ai';
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": displayTitle.value || 'Music Collection',
    "description": t(`meta.musicSection.description_${sectionType}`, { section: displayTitle.value }),
    "url": `${baseUrl}${route.fullPath}`,
    "mainEntity": {
      "@type": "MusicPlaylist",
      "name": displayTitle.value || 'Music Collection',
      "numTracks": musicList.value?.length || 0,
      "track": (musicList.value || []).map((music: Music, index: number) => ({
        "@type": "MusicRecording",
        "position": index + 1,
        "name": music.title || music.metadata?.prompt || '',
        "duration": music.metadata?.duration ? `PT${Math.floor(music.metadata.duration / 60)}M${Math.floor(music.metadata.duration % 60)}S` : undefined,
        "genre": music.metadata?.tags?.split(',').map((tag: string) => tag.trim()) || [],
        "description": music.metadata?.prompt || '',
        "url": `${baseUrl}/music/song/${music.id}`
      }))
    }
  };
});

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: () => JSON.stringify(structuredData.value)
    }
  ]
});

// 监听路由变化，当页码参数变化时重新加载数据
watch(() => route.query.page, (newPage) => {
  const pageNum = parseInt(newPage as string) || 1;
  if (pageNum !== currentPage.value) {
    currentPage.value = pageNum;
    loadData(pageNum);
  }
}, { immediate: false });

// 修改导航函数
const navigateToPage = (page: number) => {
  if (page < 1 || page > totalPages.value) return;

  if (sectionParam) {
    router.push({ path: route.path, query: { page: page.toString() } });
  } else {
    const query = { ...route.query, page: page.toString() };
    router.push({ path: route.path, query });
  }
};

// 推荐分类（用于空结果时展示和底部相关链接）
const recommendedSections = [
  { path: 'pop', title: 'Pop' },
  { path: 'rock', title: 'Rock' },
  { path: 'hip-hop', title: 'Hip Hop' },
  { path: 'electronic', title: 'Electronic' },
  { path: 'jazz', title: 'Jazz' },
  { path: 'r-and-b', title: 'R&B' },
  { path: 'anime', title: 'Anime' },
  { path: 'country', title: 'Country' },
  { path: 'lofi', title: 'Lo-Fi' },
];

// 相关分类（排除当前 section）
const relatedCategories = computed(() =>
  recommendedSections.filter(s => s.path !== sectionParam).slice(0, 6)
);

// Prompt templates 根据 section 生成
const sectionPromptTemplates: Record<string, Array<{ title: string; prompt: string }>> = {
  pop: [
    { title: 'Cheerful Pop Song', prompt: 'A cheerful and catchy pop song with upbeat energy and sing-along chorus' },
    { title: 'Romantic Pop Ballad', prompt: 'A romantic pop ballad with emotional vocals and heartfelt lyrics about love' },
    { title: 'Dance Pop Track', prompt: 'An energetic dance pop track with infectious beat and club-ready production' },
    { title: 'Indie Pop', prompt: 'A dreamy indie pop song with jangly guitars and introspective lyrics' },
    { title: 'Synth Pop', prompt: 'A retro synth-pop track with 80s-inspired synthesizers and catchy melodies' },
  ],
  rock: [
    { title: 'Energetic Rock Song', prompt: 'A powerful rock song with driving guitars, thundering drums, and raw energy' },
    { title: 'Cinematic Rock Intro', prompt: 'An epic cinematic rock intro with building tension and soaring guitar solo' },
    { title: 'Acoustic Rock Ballad', prompt: 'A heartfelt acoustic rock ballad that builds into a full band arrangement' },
    { title: 'Punk Rock', prompt: 'A fast and furious punk rock track with rebellious attitude and three-chord energy' },
    { title: 'Classic Rock Anthem', prompt: 'A classic rock anthem with soaring guitar riffs and fist-pumping chorus' },
  ],
  anime: [
    { title: 'Anime Opening Theme', prompt: 'An energetic anime opening theme with J-Pop vocals and dramatic build-ups' },
    { title: 'Emotional Anime Ending', prompt: 'A beautiful and melancholic anime ending theme with soft piano and strings' },
    { title: 'Battle Anime BGM', prompt: 'An intense battle theme for anime with orchestral power and driving rhythm' },
    { title: 'Slice of Life Theme', prompt: 'A cheerful and warm anime theme for a slice-of-life series' },
    { title: 'Romance Anime Insert', prompt: 'A tender romantic insert song for an anime confession scene' },
  ],
  jazz: [
    { title: 'Smooth Jazz Evening', prompt: 'A smooth jazz track with silky saxophone, electric piano, and gentle drums' },
    { title: 'Late-Night Jazz', prompt: 'A slow and intimate jazz ballad with breathy saxophone and sparse piano' },
    { title: 'Bossa Nova', prompt: 'A breezy bossa nova track with nylon guitar and Brazilian jazz rhythm' },
    { title: 'Jazz Fusion', prompt: 'A funky jazz fusion track blending complex harmonies with electric guitar' },
    { title: 'Jazz Club Trio', prompt: 'A classic jazz trio performance with upright bass, piano, and brushed drums' },
  ],
  country: [
    { title: 'Heartfelt Country Song', prompt: 'A heartfelt country song with acoustic guitar and storytelling lyrics' },
    { title: 'Country Road Trip', prompt: 'An upbeat country song about driving down back roads with windows down' },
    { title: 'Country Love Song', prompt: 'A sweet country love song about finding your person in a small town' },
    { title: 'Modern Country-Pop', prompt: 'A modern country-pop crossover with catchy hooks and Nashville production' },
    { title: 'Outlaw Country', prompt: 'A gritty outlaw country song with rebellious attitude and raw guitar' },
  ],
  lofi: [
    { title: 'Study Lo-Fi Beat', prompt: 'A mellow lo-fi beat perfect for studying with soft piano and vinyl crackle' },
    { title: 'Rainy Day Lo-Fi', prompt: 'A cozy lo-fi track with rain sounds, warm bass, and melancholic piano' },
    { title: 'Coffee Shop Vibes', prompt: 'A warm lo-fi track capturing the atmosphere of a cozy coffee shop' },
    { title: 'Late Night Coding', prompt: 'A minimal lo-fi beat for late-night coding with ambient pads' },
    { title: 'Sunset Lo-Fi', prompt: 'A dreamy nostalgic lo-fi beat with guitar samples and tape saturation' },
  ],
  electronic: [
    { title: 'Festival EDM', prompt: 'A massive festival EDM track with epic build-ups and huge drops' },
    { title: 'Deep House', prompt: 'A smooth deep house track with warm bass and shuffling hi-hats' },
    { title: 'Melodic Techno', prompt: 'A driving melodic techno track with arpeggiated synths and pulsing bass' },
    { title: 'Future Bass', prompt: 'A colorful future bass track with lush chords and bouncy supersaws' },
    { title: 'Ambient Electronic', prompt: 'A spacious ambient electronic piece with evolving textures and pads' },
  ],
};

const promptTemplates = computed(() => {
  const key = sectionParam?.toLowerCase();
  if (sectionPromptTemplates[key]) return sectionPromptTemplates[key];
  return [
    { title: `Create a ${chartTitle.value} Song`, prompt: `A unique ${chartTitle.value} song with creative melodies and engaging rhythm` },
    { title: `Upbeat ${chartTitle.value}`, prompt: `An upbeat and energetic ${chartTitle.value} track with catchy hooks` },
    { title: `Chill ${chartTitle.value}`, prompt: `A relaxed and chill ${chartTitle.value} song perfect for unwinding` },
    { title: `Emotional ${chartTitle.value}`, prompt: `An emotional and heartfelt ${chartTitle.value} piece with deep lyrics` },
    { title: `Modern ${chartTitle.value}`, prompt: `A modern ${chartTitle.value} track with contemporary production style` },
  ];
});

// FAQ
const faqItems = computed(() => [
  {
    question: sectionType === 'rank' ? `What is in ${chartTitle.value}?` : `What is ${chartTitle.value} AI music?`,
    answer: sectionType === 'rank'
      ? `This collection brings together songs on MusicMint. Open a track to view its creator, style, and song details.`
      : `These songs were generated with AI using ${chartTitle.value} style descriptions. Open a track to see its specific style, lyrics, and creator.`,
  },
  {
    question: sectionType === 'rank' ? 'How can I create a song inspired by a track?' : `Can I create a similar ${chartTitle.value} song?`,
    answer: `Click "Create Similar" on a song or choose a prompt idea. Review and edit the style, mood, and lyrics in Create before you generate your own song.`,
  },
  {
    question: `Can I download these ${chartTitle.value} songs?`,
    answer: `Yes, you can download AI-generated songs from MusicMint. Free users get limited downloads, while subscribers have unlimited access to high-quality audio files.`,
  },
  {
    question: `Can I use AI-generated ${chartTitle.value} music commercially?`,
    answer: `Yes! Music generated on MusicMint can be used for personal and commercial projects including YouTube videos, podcasts, social media content, and more.`,
  },
]);

// Create CTA handler
const handleSectionCreate = (prompt?: string) => {
  trackEvent('music_section_create_click', { source: 'section', page_path: route.path, slug: sectionParam, tags: sectionParam });
  saveMusicData({
    prompt: prompt || `A ${chartTitle.value} song`,
    tags: sectionParam || chartTitle.value,
    mode: 'normal',
    source: 'section_page',
    sourcePage: route.path,
  }, route.path);
};

// Page view tracking (client only, once)
onMounted(() => {
  trackEvent('music_section_view', { source: 'section', page_path: route.path, slug: sectionParam, tags: sectionParam });
});
</script>

<template>
  <div ref="sectionRoot" class="music-list-page collection-page mm-root explore-surface">
    <ExploreHeader v-if="isDailyChallenge" v-model="exploreQuery" @submit="query => query && router.push({ path: '/music/search', query: { q: query } })" />
    <div>
      <DailyChallenge v-if="isDailyChallenge" :chart="competition" />
      <div v-else class="chart-wrap">
      <header class="sec-head collection-heading">
        <h1 ref="sectionHeading" class="t-h2" tabindex="-1">{{ displayTitle || t('music.library') }} <span class="t-serif">{{ t('t24e.updatedDaily') }}</span></h1>
      </header>
      <ChartRail collection :selected="sectionParam" :summary="ssrBundle?.summary" :summary-locale="ssrBundle?.locale" />
      <div class="collection-toolbar">
        <div class="collection-meta"><span>{{ t('t37Comparison.songs', { count: musicList.length }) }}</span><RankingUpdated :window-end="windowEnd" /></div>
        <CollectionActions compact shuffle :tracks="musicList" :title="displayTitle || t('music.library')" surface="chart" />
      </div>

      <!-- 有数据时正常展示 -->
      <template v-if="musicList.length > 0 || isLoading || error">
        <MusicList
          variant="collection"
          source="chart"
          :ranking-key="sectionRankingKey"
          :rank-offset="(currentPage - 1) * PAGE_SIZE"
          :music-list="musicList"
          :is-loading="isLoading"
          :is-loading-more="isLoadingMore"
          :has-more="hasMore"
          :error="error"
          :show-create-similar="true"
          :auto-load-more="false"
          @reload="() => loadData(currentPage)"
          @load-more="loadMore"
        />
      </template>

      <!-- 空结果状态 -->
      <template v-else-if="!isLoading && musicList.length === 0">
        <MmFeedbackState heading="h2" :title="t(isChart ? 'ux.collectionUi.rankEmpty' : 'ux.collectionUi.genreEmpty', { name: displayTitle })" :description="t(isChart ? 'ux.collectionUi.rankEmptyDescription' : 'ux.collectionUi.genreEmptyDescription', { name: displayTitle })">
          <NuxtLink to="/create" class="btn btn-primary">{{ isChart ? t('ux.collectionUi.create') : t('ux.collectionUi.genreCreate', { name: displayTitle }) }}</NuxtLink>
          <div class="collection-suggestions">
            <h3>{{ t('ux.collectionUi.moreGenres') }}</h3>
            <div class="cluster"><MmChip v-for="section in recommendedSections" :key="section.path" :to="`/music/${section.path}`">{{ categoryLabel(section) }}</MmChip></div>
          </div>
        </MmFeedbackState>
      </template>

      </div>

      <ChartRail v-if="isDailyChallenge" :selected="sectionParam" :competition="competition" :summary="ssrBundle?.summary" :summary-locale="ssrBundle?.locale" />

      <!-- 对搜索引擎可见但对用户隐藏的分页导航 -->
      <div aria-hidden="true" class="sr-only">
        <div class="pagination-index">
          <span>{{ t('common.page') }}: {{ currentPage }} / {{ totalPages }}</span>
          <ul>
            <li v-if="currentPage > 1">
              <a tabindex="-1" :href="`${route.path}?page=1`" @click.prevent="navigateToPage(1)">
                {{ t('common.firstPage') }}
              </a>
            </li>
            <li v-if="currentPage > 1">
              <a tabindex="-1" :href="`${route.path}?page=${currentPage - 1}`" @click.prevent="navigateToPage(currentPage - 1)">
                {{ t('common.prevPage') }}
              </a>
            </li>
            <li v-for="page in Math.min(10, totalPages)" :key="page">
              <a tabindex="-1" :href="`${route.path}?page=${page}`" @click.prevent="navigateToPage(page)">
                {{ page }}
              </a>
            </li>
            <li v-if="currentPage < totalPages">
              <a tabindex="-1" :href="`${route.path}?page=${currentPage + 1}`" @click.prevent="navigateToPage(currentPage + 1)">
                {{ t('common.nextPage') }}
              </a>
            </li>
            <li v-if="currentPage < totalPages">
              <a tabindex="-1" :href="`${route.path}?page=${totalPages}`" @click.prevent="navigateToPage(totalPages)">
                {{ t('common.lastPage') }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <!-- 可见分页控件 -->
      <div v-if="totalPages > 1 && !isDailyChallenge" class="collection-pagination flex justify-center items-center mt-8 mb-4 gap-3 flex-wrap">
        <MmButton variant="secondary"
          v-if="currentPage > 1"
          @click="navigateToPage(currentPage - 1)"
        >
          {{ t('common.prevPage') }}
        </MmButton>

        <div class="flex items-center gap-1.5">
          <input
            ref="pageInput"
            type="number"
            :value="currentPage"
            min="1"
            :max="totalPages"
            :aria-label="t('common.page')"
            class="w-12 h-11 text-center text-sm rounded-lg border focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            @keydown.enter="($event) => { const v = parseInt(($event.target as HTMLInputElement).value); if (v >= 1 && v <= totalPages) navigateToPage(v); }"
          />
          <span class="text-sm">/ {{ totalPages }}</span>
          <MmButton variant="secondary"
            @click="() => { const v = parseInt(($refs.pageInput as HTMLInputElement)?.value); if (v >= 1 && v <= totalPages) navigateToPage(v); }"
          >{{ t('ux.collectionUi.go') }}</MmButton>
        </div>

        <MmButton variant="secondary"
          v-if="currentPage < totalPages"
          @click="navigateToPage(currentPage + 1)"
        >
          {{ t('common.nextPage') }}
        </MmButton>
      </div>

      <FeedEntry banner :covers="musicList.filter(song => song.image_url).slice(4, 7).map(song => song.image_url)" />
      <GenreDirectory />

      <section class="section collection-support collection-seo" aria-labelledby="collection-about">
        <h2 id="collection-about" class="t-h2">{{ displayTitle || t('music.library') }}</h2>
        <p>{{ seoIntroText }}</p>
        <p v-if="sectionRankingKey && te(`rankingDescriptions.${sectionRankingKey}`)">{{ t(`rankingDescriptions.${sectionRankingKey}`) }}</p>
        <p v-else-if="isChart">{{ t('t24d.chartDescription') }}</p>
        <RankingUpdated :window-end="windowEnd" />
        <MmButton variant="secondary" type="button" @click="handleSectionCreate()">{{ isChart ? t('ux.collectionUi.create') : t('ux.collectionUi.genreCreate', { name: displayTitle }) }}</MmButton>
        <NuxtLink v-if="!isDailyChallenge" class="see-all" to="/music/ranking-music_daily_competition">{{ t('mm.challenge.title') }}</NuxtLink>
      </section>
      <!-- Prompt Templates -->
      <section class="collection-support mt-12 mb-10">
        <h2 class="text-xl font-semibold mb-4">
          {{ t('ux.collectionUi.ideas', { name: displayTitle }) }}
        </h2>
        <p class="collection-language-note">{{ t('ux.collectionUi.englishNote') }}</p>
        <div class="collection-ideas" lang="en">
          <button
            v-for="(template, idx) in promptTemplates"
            :key="idx"
            type="button"
            class="collection-idea text-left w-full rounded-lg border transition-colors cursor-pointer group duration-quick ease-out"
            @click="handleSectionCreate(template.prompt)"
          >
            <span class="block text-sm font-medium mb-1 transition-colors duration-quick ease-out">
              {{ template.title }}
            </span>
            <span class="block text-sm">{{ template.prompt }}</span>
          </button>
        </div>
      </section>

      <!-- FAQ -->
      <section class="collection-support mb-10">
        <h2 class="text-xl font-semibold mb-4">
          {{ t('ux.collectionUi.faq') }}
        </h2>
        <div class="space-y-3" lang="en">
          <details
            v-for="(item, idx) in faqItems"
            :key="idx"
            class="group rounded-lg border overflow-hidden"
          >
            <summary class="px-5 py-3 cursor-pointer text-sm font-medium flex items-center justify-between transition-colors duration-quick ease-out">
              <span>{{ item.question }}</span>
              <svg class="w-4 h-4 transition-transform group-open:rotate-180 shrink-0 ml-2 duration-base ease-out" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div class="collection-answer px-5 pb-4 leading-relaxed">
              {{ item.answer }}
            </div>
          </details>
        </div>
      </section>

      <!-- Related Categories -->
      <section class="mb-8">
        <h2 class="text-lg font-semibold mb-3">
          {{ t('ux.collectionUi.moreGenres') }}
        </h2>
        <div class="flex flex-wrap gap-2">
          <NuxtLink
            v-for="cat in relatedCategories"
            :key="cat.path"
            :to="`/music/${cat.path}`"
            class="px-4 py-2 rounded-pill text-sm border transition-colors duration-quick ease-out"
          >
            {{ categoryLabel(cat) }}
          </NuxtLink>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.collection-suggestions { width: 100%; margin-top: var(--s-6); }
.collection-suggestions h3 { margin-bottom: var(--s-4); font: 600 var(--t-small)/1.4 var(--font-ui); }
.collection-suggestions .cluster { justify-content: center; }
.collection-page { max-width: var(--maxw); margin-inline: auto; color: var(--text); }
.chart-wrap { display: grid; gap: var(--s-6); align-items: start; margin-bottom: 0; }
.collection-page .collection-heading { margin: 0; padding-block: var(--s-4) var(--s-1); }
.collection-toolbar { display: flex; flex-wrap: nowrap; align-items: center; gap: var(--s-2); min-width: 0; }
.collection-meta { display: flex; align-items: baseline; gap: var(--s-1); flex: 1; min-width: 0; color: var(--text-2); font: 500 var(--t-micro)/1.5 var(--font-mono); white-space: nowrap; }
.collection-meta > span { flex: 0 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.collection-meta :deep(.ranking-updated) { display: block; flex: 1 1 0; min-width: 0; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.collection-meta :deep(.ranking-updated)::before { content: '· '; }
.collection-toolbar :deep(.collection-actions) { flex-shrink: 0; }
.collection-language-note { margin: 0 0 var(--s-4); color: var(--text-2); font-size: var(--t-small); }
.collection-support h2 { font: 700 var(--t-h2)/1.3 var(--font-display); }
.collection-support button, .collection-support details { background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-md); }
.collection-support button:hover { border-color: var(--accent); }
.collection-support button span:last-child { color: var(--text-2); }
.collection-ideas { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 224px), 1fr)); gap: var(--s-3); }
.collection-idea { display: flex; flex-direction: column; gap: var(--s-2); padding: var(--s-4); }
.collection-idea span:first-child { font-size: var(--t-body); }
.collection-answer { max-width: 68ch; color: var(--text-2); font-size: var(--t-body); }
.collection-pagination input { width: 64px; height: 44px; appearance: textfield; }
.collection-pagination input::-webkit-outer-spin-button, .collection-pagination input::-webkit-inner-spin-button { appearance: none; margin: 0; }
.collection-support summary { min-height: 52px; }
.collection-page input:not([type="search"]) { font-size: 16px; background: var(--surface); border: 1px solid var(--line-strong); border-radius: var(--r-sm); }
.collection-page :is(a,button,input,summary):focus-visible { outline: 2px solid var(--focus); outline-offset: 3px; }
.collection-page a.rounded-pill { display: inline-flex; align-items: center; min-height: 44px; }
.collection-page .collection-support + section a { display: inline-flex; align-items: center; background: var(--surface); border: 1px solid var(--line); }
.chart-wrap:not(.daily-challenge) { gap: var(--s-2); }
</style>
